import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { VideoCodecInfo } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { codecName, codecOfUrl } from "../lib/video-codec.ts";
import { Icon } from "./Icon.tsx";

export const SPEEDS = [0.25, 0.5, 0.75, 1] as const;
/** One frame of a 60 fps clip: the step of the frame buttons. */
const FRAME = 1 / 60;

interface Prefs {
  speed: number;
  volume: number;
  muted: boolean;
  loop: boolean;
}

// Speed, sound and loop carry over from one video to the next, in this browser.
const KEY = "69ly:player";
function loadPrefs(): Prefs {
  const fallback: Prefs = { speed: 1, volume: 0.8, muted: false, loop: true };
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? "null") as Partial<Prefs> | null;
    return {
      speed: SPEEDS.includes(p?.speed as (typeof SPEEDS)[number]) ? p!.speed! : fallback.speed,
      volume: typeof p?.volume === "number" ? Math.min(1, Math.max(0, p.volume)) : fallback.volume,
      muted: typeof p?.muted === "boolean" ? p.muted : fallback.muted,
      loop: typeof p?.loop === "boolean" ? p.loop : fallback.loop,
    };
  } catch {
    return fallback;
  }
}
function savePrefs(p: Prefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    // Private windows: just for this visit.
  }
}

/** 3.25 → "0:03.2": tenths matter when you study a throw. */
export function clipTime(seconds: number): string {
  const s = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  const m = Math.floor(s / 60);
  const rest = s - m * 60;
  return `${m}:${rest.toFixed(1).padStart(4, "0")}`;
}

/** How long a video behind the pictures waits before it starts loading. */
const HIDDEN_DELAY_MS = 600;

/** How long play waits, at most, for the browser to have enough to play through without stopping. */
const MAX_WAIT_MS = 2500;

/** Playing this long with no picture decoded: the browser cannot show this video's picture. */
const NO_PICTURE_AFTER_MS = 1500;

/**
 * Why a video does not show: it failed to play at all, or plays its sound without
 * a picture (a codec the browser cannot decode, H.265 most often). Reads the codec
 * from the file to say which, and how to fix it on either side.
 */
function ClipTrouble({ kind, src }: { kind: "failed" | "blind"; src: string }) {
  const { t } = useTranslation();
  const [codec, setCodec] = useState<VideoCodecInfo | null>(null);
  useEffect(() => {
    let live = true;
    void codecOfUrl(src).then((c) => live && setCodec(c));
    return () => {
      live = false;
    };
  }, [src]);
  const name = codec ? codecName(codec) : null;
  return (
    <div className={`clip-message is-${kind}`} role="alert">
      {kind === "blind" ? (
        <>
          <p>
            <b>{t("player.noPicture")}</b>
          </p>
          <p className="small">{name ? t("player.noPictureWhy", { codec: name }) : t("player.noPictureWhyUnknown")}</p>
        </>
      ) : (
        <p className="small">{name && codec?.codec !== "h264" ? t("player.cannotPlayWhy", { codec: name }) : t("player.cannotPlay")}</p>
      )}
      {(kind === "blind" || codec?.codec === "hevc") && (
        <ul className="clip-fixes small">
          <li>{t("player.fixViewer")}</li>
          <li>{t("player.fixAuthor")}</li>
        </ul>
      )}
      <a className="btn btn-secondary btn-small" href={src} download>
        <Icon name="download" size={14} />
        {t("player.download")}
      </a>
    </div>
  );
}

/** Light loading on a data saver or a slow mobile connection: just enough to show the length. */
function lightLoading(): boolean {
  const c = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection;
  return Boolean(c?.saveData) || c?.effectiveType === "slow-2g" || c?.effectiveType === "2g";
}

/** The seconds of video the browser holds from the current position on. */
function bufferedAhead(v: HTMLVideoElement): number {
  let end = v.currentTime;
  for (let i = 0; i < v.buffered.length; i++) if (v.buffered.start(i) <= v.currentTime + 0.25) end = Math.max(end, v.buffered.end(i));
  return end;
}

/**
 * The player for uploaded videos: slow motion, frame by frame, loop, sound and
 * full screen on the player itself. Keys: Space, ← →, , . (frames), M, L, F.
 *
 * It streams the clip like any web video, starting to load it as soon as it is
 * on the page (even hidden, behind the pictures), with a picture of the throw
 * until it plays. Play starts once the browser says it can play to the end
 * without stopping, or after a short wait at most: no long download first, and
 * no stutter at the start.
 *
 * With `autoPlay` (Live), it starts by itself once shown, silent and looping
 * whatever the saved settings say: a sound in the room while you play is the
 * last thing you want. Unmuting is a press away, and is not saved.
 */
export function ClipPlayer({ src, title, poster, active = true, autoPlay = false }: { src: string; title: string; poster?: string; active?: boolean; autoPlay?: boolean }) {
  const { t } = useTranslation();
  const wrap = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [playing, setPlaying] = useState(false);
  // Play was pressed: it starts once enough of the clip is there.
  const [wantsPlay, setWantsPlay] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [failed, setFailed] = useState(false);
  // Plays its sound but cannot show the picture (a codec this browser cannot decode).
  const [blind, setBlind] = useState(false);
  // The same, for play calls already on their way (autoplay): they must not start the sound.
  const blindNow = useRef(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  // How far the browser has the video, in seconds from the start.
  const [buffered, setBuffered] = useState(0);
  const [light] = useState(lightLoading);
  // Behind the pictures, the video waits a moment before loading: the pictures come first.
  const [armed, setArmed] = useState(active);
  useEffect(() => {
    if (armed) return;
    if (active) return setArmed(true);
    const timer = setTimeout(() => setArmed(true), HIDDEN_DELAY_MS);
    return () => clearTimeout(timer);
  }, [armed, active]);
  // Gone (another stuff, another page): its download stops now. A video taken off the
  // page keeps downloading until the browser gets round to freeing it, holding a
  // connection the rest of the page needs.
  const srcNow = useRef<string | undefined>(undefined);
  srcNow.current = armed ? src : undefined;
  useEffect(() => {
    const v = video.current;
    // Mounted again on the same element (React's development double run): its source back.
    if (v && srcNow.current && !v.getAttribute("src")) v.setAttribute("src", srcNow.current);
    return () => {
      if (!v) return;
      v.pause();
      v.removeAttribute("src");
      v.load();
    };
  }, []);
  // Playing on its own: silent and looping until told otherwise, for this clip only.
  const [quiet, setQuiet] = useState(autoPlay);
  const [loopNow, setLoopNow] = useState<boolean | null>(autoPlay ? true : null);
  // Started by itself, not by a press: no "Starting…" for it.
  const [auto, setAuto] = useState(false);

  // Another clip: a fresh start.
  const [seen, setSeen] = useState(src);
  if (seen !== src) {
    setSeen(src);
    setPlaying(false);
    setWantsPlay(false);
    setFailed(false);
    setBlind(false);
    blindNow.current = false;
    setTime(0);
    setDuration(0);
    setBuffered(0);
    setQuiet(autoPlay);
    setLoopNow(autoPlay ? true : null);
  }
  const muted = prefs.muted || quiet;
  const loop = loopNow ?? prefs.loop;

  // While it plays, the controls fade out after a moment without the pointer moving.
  const [idle, setIdle] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const wake = () => {
    setIdle(false);
    clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setIdle(true), 2500);
  };
  useEffect(() => () => clearTimeout(idleTimer.current), []);
  // A tap on a phone while the controls are hidden only brings them back.
  const tap = useRef({ touch: false, wasIdle: false });

  // The element follows the preferences.
  useEffect(() => {
    const v = video.current;
    if (!v) return;
    v.playbackRate = prefs.speed;
    v.volume = prefs.volume;
    v.muted = muted;
    v.loop = loop;
  }, [prefs, muted, loop]);

  // Hidden (another picture shown): it stops, and stops waiting to start.
  useEffect(() => {
    if (active) return;
    video.current?.pause();
    setWantsPlay(false);
  }, [active]);

  // Playing on its own: as soon as it is shown, a new clip included; not on a data saver.
  useEffect(() => {
    if (!autoPlay || !active || light) return;
    setAuto(true);
    setWantsPlay(true);
  }, [autoPlay, active, light, src]);

  // The position moves with every frame while it plays, not four times a second.
  useEffect(() => {
    if (!playing) return;
    let frame = 0;
    const tick = () => {
      const v = video.current;
      if (v) setTime(v.currentTime);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  // No picture: stop the sound too, and say why rather than leave the still picture up.
  const noPicture = useCallback(() => {
    blindNow.current = true;
    video.current?.pause();
    setWantsPlay(false);
    setWaiting(false);
    setBlind(true);
  }, []);
  // Some browsers drop a picture they cannot decode and play on: no frame after a moment of play gives it away.
  useEffect(() => {
    if (!playing || blind) return;
    const timer = setTimeout(() => {
      const v = video.current;
      const frames = v?.getVideoPlaybackQuality?.().totalVideoFrames;
      if (v && !v.paused && v.currentTime > 0.5 && frames === 0) noPicture();
    }, NO_PICTURE_AFTER_MS);
    return () => clearTimeout(timer);
  }, [playing, blind, noPicture]);

  const start = useCallback(() => {
    const v = video.current;
    if (!v || blindNow.current) return;
    setWantsPlay(false);
    v.play().catch((err: unknown) => {
      // The browser wants a fresh tap: the big button is back.
      if (err instanceof DOMException && err.name === "AbortError") return;
      setPlaying(false);
    });
  }, []);

  // Play was pressed: start as soon as the browser can play to the end, the whole clip is in, or the wait is over.
  useEffect(() => {
    const v = video.current;
    if (!wantsPlay || !v) return;
    const ready = () => v.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA || (v.duration > 0 && bufferedAhead(v) >= v.duration - 0.05);
    if (ready()) return start();
    const check = () => {
      if (ready()) start();
    };
    const timer = setTimeout(() => {
      if (v.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) start();
      else v.addEventListener("canplay", start, { once: true });
    }, MAX_WAIT_MS);
    v.addEventListener("canplaythrough", start);
    v.addEventListener("progress", check);
    // Light loading only fetched the start: now it is wanted, whole.
    if (v.preload !== "auto") {
      v.preload = "auto";
      if (v.readyState < HTMLMediaElement.HAVE_FUTURE_DATA) v.load();
    }
    return () => {
      clearTimeout(timer);
      v.removeEventListener("canplaythrough", start);
      v.removeEventListener("progress", check);
      v.removeEventListener("canplay", start);
    };
  }, [wantsPlay, start]);

  const update = (change: Partial<Prefs>) =>
    setPrefs((p) => {
      const next = { ...p, ...change };
      savePrefs(next);
      return next;
    });

  const toggle = () => {
    const v = video.current;
    if (!v || failed || blind) return;
    setAuto(false);
    // Pressed again while it waits: play now, whatever is there.
    if (wantsPlay) return start();
    if (v.paused) setWantsPlay(true);
    else v.pause();
  };
  const seek = (to: number) => {
    const v = video.current;
    if (!v) return;
    v.currentTime = Math.min(Math.max(0, to), v.duration || 0);
    setTime(v.currentTime);
  };
  const step = (frames: number) => {
    video.current?.pause();
    setWantsPlay(false);
    seek((video.current?.currentTime ?? 0) + frames * FRAME);
  };
  const fullscreen = () => {
    if (document.fullscreenElement) void document.exitFullscreen();
    else void wrap.current?.requestFullscreen?.().catch(() => undefined);
  };

  const silent = muted || prefs.volume === 0;
  const toggleSound = () => {
    if (silent) {
      setQuiet(false);
      update({ muted: false, volume: prefs.volume || 0.8 });
    } else update({ muted: true });
  };
  const toggleLoop = () => (loopNow !== null ? setLoopNow(!loop) : update({ loop: !prefs.loop }));

  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    // Sliders and buttons keep their own keys.
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" && e.key !== " ") return;
    const keys: Record<string, () => void> = {
      " ": toggle,
      k: toggle,
      ArrowLeft: () => seek((video.current?.currentTime ?? 0) - 1),
      ArrowRight: () => seek((video.current?.currentTime ?? 0) + 1),
      ",": () => step(-1),
      ".": () => step(1),
      m: toggleSound,
      l: toggleLoop,
      f: fullscreen,
    };
    const run = keys[e.key];
    if (run && !(target.tagName === "BUTTON" && e.key === " ")) {
      e.preventDefault();
      run();
    }
  };

  const loaded = duration ? Math.min(100, Math.round((buffered / duration) * 100)) : 0;
  return (
    <div
      ref={wrap}
      className={`clip-player${playing && idle ? " is-idle" : ""}`}
      onKeyDown={(e) => {
        wake();
        onKey(e);
      }}
      onPointerMove={(e) => e.pointerType === "mouse" && wake()}
      onPointerDown={(e) => {
        tap.current = { touch: e.pointerType !== "mouse", wasIdle: playing && idle };
        wake();
      }}
      onFocus={wake}
    >
      <div className="clip-screen">
        <video
          ref={video}
          src={armed ? src : undefined}
          poster={poster}
          preload={light ? "metadata" : "auto"}
          playsInline
          aria-label={title}
          onClick={() => {
            if (tap.current.touch && tap.current.wasIdle) return;
            toggle();
          }}
          onPlay={(e) => {
            // No picture to show: no sound on its own either.
            if (blindNow.current) e.currentTarget.pause();
            else setPlaying(true);
          }}
          onPlaying={() => setWaiting(false)}
          onPause={() => setPlaying(false)}
          onEmptied={() => setPlaying(false)}
          onWaiting={() => setWaiting(true)}
          onCanPlay={() => setWaiting(false)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          onLoadedMetadata={(e) => {
            const v = e.currentTarget;
            // A video with no picture the browser can draw: the sound alone would play.
            if (v.videoWidth === 0 && v.videoHeight === 0) noPicture();
            setDuration(v.duration);
            v.playbackRate = prefs.speed;
            v.volume = prefs.volume;
            v.muted = muted;
            v.loop = loop;
          }}
          onError={() => {
            setPlaying(false);
            setWaiting(false);
            setWantsPlay(false);
            setFailed(true);
          }}
          onProgress={(e) => setBuffered(bufferedAhead(e.currentTarget))}
          onSeeked={(e) => setBuffered(bufferedAhead(e.currentTarget))}
          onDurationChange={(e) => setDuration(e.currentTarget.duration)}
          onVolumeChange={(e) => {
            const v = e.currentTarget;
            if (v.muted !== muted || Math.abs(v.volume - prefs.volume) > 0.001) update({ muted: v.muted, volume: v.volume });
          }}
        />
        {failed || blind ? (
          <ClipTrouble kind={failed ? "failed" : "blind"} src={src} />
        ) : (
          !playing && (
            <button
              type="button"
              className={`clip-big-play${wantsPlay ? " is-starting" : ""}`}
              aria-label={wantsPlay ? t("player.startNow") : t("player.play")}
              title={wantsPlay ? t("player.startNow") : undefined}
              onClick={toggle}
              style={{ ["--loaded" as string]: `${loaded}%` }}
            >
              {loaded < 100 && <span className="clip-ring" aria-hidden="true" />}
              <Icon name="play" size={28} />
            </button>
          )
        )}
        {wantsPlay && !auto && !playing && !failed && !blind && (
          <span className="clip-loading small" aria-live="polite">
            {t("player.starting")}
          </span>
        )}
        {playing && waiting && <span className="clip-spinner" aria-label={t("player.buffering")} role="status" />}
        <div className="clip-controls" role="group" aria-label={t("player.controls")}>
          <button type="button" className="clip-btn" aria-label={playing ? t("player.pause") : t("player.play")} title={`${playing ? t("player.pause") : t("player.play")} (Space)`} onClick={toggle}>
            <Icon name={playing ? "pause" : "play"} />
          </button>
          <button type="button" className="clip-btn clip-frame" aria-label={t("player.frameBack")} title={`${t("player.frameBack")} (,)`} onClick={() => step(-1)}>
            <Icon name="frameBack" />
          </button>
          <button type="button" className="clip-btn clip-frame" aria-label={t("player.frameNext")} title={`${t("player.frameNext")} (.)`} onClick={() => step(1)}>
            <Icon name="frameNext" />
          </button>
          <input
            className="clip-seek"
            type="range"
            min={0}
            max={duration || 0}
            step={0.01}
            value={Math.min(time, duration || 0)}
            aria-label={t("player.position")}
            aria-valuetext={`${clipTime(time)} / ${clipTime(duration)}`}
            onChange={(e) => seek(Number(e.target.value))}
            style={{
              ["--done" as string]: `${duration ? (Math.min(time, duration) / duration) * 100 : 0}%`,
              ["--buffered" as string]: `${loaded}%`,
            }}
          />
          <span className="clip-time mono" aria-hidden="true">
            {clipTime(time)} / {clipTime(duration)}
          </span>
          <div className="clip-speeds" role="group" aria-label={t("player.speed")}>
            {SPEEDS.map((s) => (
              <button key={s} type="button" className="clip-speed" aria-pressed={prefs.speed === s} onClick={() => update({ speed: s })}>
                {s}×
              </button>
            ))}
          </div>
          {/* Narrow players: one button that goes through the speeds. */}
          <button
            type="button"
            className="clip-speed clip-speed-cycle"
            aria-label={t("player.speedNow", { speed: `${prefs.speed}×` })}
            title={t("player.speed")}
            onClick={() => update({ speed: SPEEDS[(SPEEDS.indexOf(prefs.speed as (typeof SPEEDS)[number]) + SPEEDS.length - 1) % SPEEDS.length] })}
          >
            {prefs.speed}×
          </button>
          <button
            type="button"
            className="clip-btn"
            aria-pressed={loop}
            aria-label={t("player.loop")}
            title={`${t("player.loop")} (L)`}
            onClick={toggleLoop}
          >
            <Icon name="loop" />
          </button>
          <div className="clip-sound">
            <button
              type="button"
              className="clip-btn"
              aria-label={silent ? t("player.unmute") : t("player.mute")}
              title={`${silent ? t("player.unmute") : t("player.mute")} (M)`}
              onClick={toggleSound}
            >
              <Icon name={silent ? "muted" : "volume"} />
            </button>
            <input
              className="clip-volume"
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={muted ? 0 : prefs.volume}
              aria-label={t("player.volume")}
              aria-valuetext={`${Math.round((muted ? 0 : prefs.volume) * 100)}%`}
              onChange={(e) => {
                setQuiet(false);
                update({ volume: Number(e.target.value), muted: Number(e.target.value) === 0 });
              }}
              style={{ ["--done" as string]: `${(muted ? 0 : prefs.volume) * 100}%` }}
            />
          </div>
          <button type="button" className="clip-btn" aria-label={t("player.fullscreen")} title={`${t("player.fullscreen")} (F)`} onClick={fullscreen}>
            <Icon name="fit" />
          </button>
        </div>
      </div>
    </div>
  );
}
